import {
  prop, evolve, map, pick,
} from 'ramda';
import { createSelector } from 'reselect';
import { isSafari } from '../util';
import { NAMESPACE, APP_INFO } from '../constants';
import { getCompany, sessionCompanies } from './companies';
import { getAgent, sessionAgent } from './agents';

/** @type {(state: import('../types').State) => import('../types').AppInfo} */
export const getAppInfo = (state) => state[APP_INFO] || {};

/** @type {(state: import('../types').State) => import('../types').SessionData['aws']} */
export const getAWS = (state) => state[NAMESPACE].aws;

/** @type {(state: import('../types').State) => string | undefined} */
export const getSelectedCompanyId = (state) => state[NAMESPACE].selectedCompany;

export const getUserAgentId = createSelector(
  getCompany(getSelectedCompanyId),
  prop('userAgent'),
);

export const getUserAgent = createSelector(
  getUserAgentId,
  getAgent(getSelectedCompanyId, getUserAgentId),
  (userAgentId, userAgent) => userAgent || (userAgentId ? { id: userAgentId } : undefined),
);

export const getHasSessionData = createSelector(
  getAWS,
  getCompany(getSelectedCompanyId),
  (aws, company) => !!(aws && company),
);

export const getSessionData = createSelector(
  getAWS,
  getSelectedCompanyId,
  sessionCompanies,
  sessionAgent(getSelectedCompanyId, getUserAgentId),
  (
    aws,
    selectedCompany,
    companies,
    userAgent,
  ) => {
    const result = { aws, selectedCompany, companies };
    if (selectedCompany && userAgent) {
      result.agents = {
        [selectedCompany]: { [userAgent.id]: userAgent },
      };
    }
    return { [NAMESPACE]: result };
  },
);

const compressSessionData = evolve({
  [NAMESPACE]: evolve({
    companies: (companies) => {
      if (!companies) {
        return undefined;
      }
      const companyIds = Object.keys(companies);
      if (companyIds.length <= 5) {
        return companies;
      }
      // Session companies always carry a token or an apikey; keep only one credential
      return companyIds.reduce((result, id) => ({
        ...result,
        [id]: pick(['id', companies[id].token ? 'token' : 'apikey', 'userAgent'], companies[id]),
      }), {});
    },
    // evolve skips the key when no agents are present
    agents: map(map(pick(['id', 'role', 'metadata']))),
  }),
});

/**
 * Builds the selector SessionMiddleware persists: the session data (compressed on Safari, where
 * cookies are the storage) merged with whatever the optional selector returns.
 * @param {(state: any) => object} [selector]
 * @returns {(state: import('../types').State) => object}
 */
export const SessionSelector = (selector) => {
  const baseSelector = isSafari()
    ? createSelector(
        getSessionData,
        compressSessionData,
      )
    : getSessionData;
  return selector
    ? (state) => ({
        ...selector(state),
        ...baseSelector(state),
      })
    : baseSelector;
};
